import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "react-oidc-context";

import {
  addReplaceAliasForTerminologyItem,
  deleteTerminologyItem,
  getTerminologyItem,
  probeTerminology,
  promoteAbbrevCandidateToReview,
  suggestAbbrevSenses,
  updateTerminologyItem,
} from "@/api/terminology";
import type {
  AbbrevSenseSuggestion,
  TerminologyProbeResponse,
} from "@/api/terminology";
import { useToast } from "@/components/toast";
import {
  buildTerminologyItemTourSteps,
  runTour,
  shouldAutoRunTour,
} from "@/tours/terminologyTour";
import { itemTypeLabel, langTagLabel, statusLabel } from "./labels";
import { TerminologyUsageBlock } from "./components/TerminologyUsageBlock";

const EDITABLE_FIELDS = [
  "source_text",
  "target_text",
  "source_lang",
  "target_lang",
  "domain",
  "confidence",
  "notes",
  // Только для abbreviation: расшифровка в source-языке + сокращение в target.
  "source_expansion",
  "target_abbreviation",
  // disambiguation-контекст смысла (слова через запятую).
  "context_words",
] as const;

type FieldKey = (typeof EDITABLE_FIELDS)[number];

export default function AdminTerminologyItem() {
  const { itemId = "" } = useParams<{ itemId: string }>();
  const navigate = useNavigate();
  const auth = useAuth();
  const { toast } = useToast();
  const qc = useQueryClient();
  const [editMode, setEditMode] = useState(false);
  const [savePrevAsAlias, setSavePrevAsAlias] = useState(false);
  const [showAliasForm, setShowAliasForm] = useState(false);
  const [aliasInput, setAliasInput] = useState("");
  const [aliasReplaceTo, setAliasReplaceTo] = useState<"text" | "abbrev">("text");
  const [suggesting, setSuggesting] = useState(false);
  const [suggestions, setSuggestions] = useState<AbbrevSenseSuggestion[] | null>(
    null,
  );
  const [probeText, setProbeText] = useState("");
  const [probing, setProbing] = useState(false);
  const [probeResult, setProbeResult] = useState<TerminologyProbeResponse | null>(
    null,
  );
  const [draft, setDraft] = useState<Record<FieldKey, string>>({
    source_text: "",
    target_text: "",
    source_lang: "",
    target_lang: "",
    domain: "",
    confidence: "",
    notes: "",
    source_expansion: "",
    target_abbreviation: "",
    context_words: "",
  });

  const query = useQuery({
    queryKey: ["terminology-item", itemId],
    queryFn: async () => {
      const r = await getTerminologyItem(itemId);
      if (!r.ok) throw new Error(r.error);
      return r.data;
    },
    enabled: Boolean(itemId),
  });

  const reviewer =
    (auth.user?.profile?.preferred_username as string | undefined) ??
    (auth.user?.profile?.email as string | undefined) ??
    "anonymous";

  const updateMutation = useMutation({
    mutationFn: async (payload: Record<string, unknown>) => {
      const r = await updateTerminologyItem(itemId, {
        reviewer,
        ...payload,
      });
      if (!r.ok) throw new Error(r.error);
      return r.data;
    },
    onSuccess: (data) => {
      toast("Изменения сохранены", "success");
      setEditMode(false);
      setSavePrevAsAlias(false);
      if (data.created_alias) {
        toast(
          `Bridge-alias создан: «${data.created_alias.alias_text}» → «${data.created_alias.canonical_text}»`,
          "success",
        );
      }
      // item_id это content-hash (см. terminology_api._item_id) — после правки
      // полей вроде target_text/domain хэш меняется, и старый URL уже мёртв.
      // Кладём свежие данные под новым ключом, инвалидируем старый и
      // навигируемся на новый URL, чтобы перерисоваться без 404.
      const newId = data.item.item_id;
      qc.setQueryData(["terminology-item", newId], data);
      void qc.invalidateQueries({ queryKey: ["terminology-items"] });
      void qc.invalidateQueries({ queryKey: ["terminology-item", itemId] });
      if (newId && newId !== itemId) {
        navigate(`/admin/terminology/${encodeURIComponent(newId)}`, {
          replace: true,
        });
      }
    },
    onError: (err: Error) => toast(`Не сохранено: ${err.message}`, "error"),
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      const r = await deleteTerminologyItem(itemId);
      if (!r.ok) throw new Error(r.error);
      return r.data;
    },
    onSuccess: () => {
      toast("Запись удалена", "success");
      void qc.invalidateQueries({ queryKey: ["terminology-items"] });
      void qc.invalidateQueries({ queryKey: ["terminology-item", itemId] });
      navigate("/admin/terminology", { replace: true });
    },
    onError: (err: Error) => toast(`Не удалено: ${err.message}`, "error"),
  });

  const addAliasMutation = useMutation({
    mutationFn: async (params: {
      alias_text: string;
      canonical_text: string;
      exact: boolean;
    }) => {
      const r = await addReplaceAliasForTerminologyItem(itemId, params);
      if (!r.ok) throw new Error(r.error);
      return r.data;
    },
    onSuccess: (data) => {
      toast(
        `Добавлено: «${data.alias_text}» → «${data.canonical_text}»`,
        "success",
      );
      setAliasInput("");
      setShowAliasForm(false);
      void qc.invalidateQueries({ queryKey: ["terminology-items"] });
      void qc.invalidateQueries({ queryKey: ["terminology-item", itemId] });
    },
    onError: (err: Error) => toast(`Не добавлено: ${err.message}`, "error"),
  });

  const promoteMutation = useMutation({
    mutationFn: async () => {
      const r = await promoteAbbrevCandidateToReview(itemId, {});
      if (!r.ok) throw new Error(r.error);
      return r.data;
    },
    onSuccess: (data) => {
      toast(`Создан review_item — открываю в очереди`, "success");
      void qc.invalidateQueries({ queryKey: ["terminology-items"] });
      // Перебрасываем reviewer'а сразу в focus-страницу нового review_item
      navigate(
        `/admin/review/focus/${encodeURIComponent(data.stable_key)}`,
      );
    },
    onError: (err: Error) =>
      toast(`Не отправилось: ${err.message}`, "error"),
  });

  const aliasDeleteMutation = useMutation({
    mutationFn: async (aliasItemId: string) => {
      const r = await deleteTerminologyItem(aliasItemId);
      if (!r.ok) throw new Error(r.error);
      return r.data;
    },
    onSuccess: () => {
      toast("Запрещённая форма удалена", "success");
      void qc.invalidateQueries({ queryKey: ["terminology-items"] });
      void qc.invalidateQueries({ queryKey: ["terminology-item", itemId] });
    },
    onError: (err: Error) =>
      toast(`Не удалось удалить: ${err.message}`, "error"),
  });

  // Автозапуск тура при первом открытии карточки (один раз на браузер).
  // Хук выше ранних return'ов — иначе порядок хуков поедет. Флаги выводим
  // прямо из query.data, т.к. item/isReadOnly объявлены ниже.
  // НЕ возвращаем cleanup с clearTimeout — в React.StrictMode он отменил бы
  // единственный запуск, а повторный проход уже видит localStorage="done"
  // (см. подробный коммент в AdminTerminology.tsx).
  useEffect(() => {
    const data = query.data;
    if (!data) return;
    if (!shouldAutoRunTour("mt:tour:terminology-item")) return;
    const it = data.item;
    const ro = String(it.item_type).endsWith("_candidate");
    const canForbidden =
      !ro &&
      (it.item_type === "glossary_term" || it.item_type === "abbreviation");
    setEditMode(false);
    if (canForbidden) setShowAliasForm(true);
    window.setTimeout(
      () =>
        runTour(
          buildTerminologyItemTourSteps({
            canHaveForbiddenForms: canForbidden,
            isReadOnly: ro,
          }),
        ),
      150,
    );
  }, [query.data]);

  if (query.isLoading) return <div className="mt-empty">Загружаем…</div>;
  if (query.isError)
    return (
      <div className="mt-empty mt-empty--error">
        {(query.error as Error).message}
      </div>
    );
  const item = query.data!.item;
  const siblings = query.data!.siblings ?? [];
  const replaceAliases = query.data!.replace_aliases ?? [];
  // Backend выбрасывает ошибку для candidate-типов; читаем по строке
  // на случай если backend пришлёт что-то ещё, что мы пока не знаем.
  const isReadOnly = String(item.item_type).endsWith("_candidate");
  const canHaveForbiddenForms =
    !isReadOnly &&
    (item.item_type === "glossary_term" || item.item_type === "abbreviation");

  function startItemTour(): void {
    // Тур подсвечивает блоки, которые рендерятся только вне edit-режима;
    // для термина/аббревиатуры заранее раскрываем форму «Запрещённые формы»,
    // чтобы её поля существовали в DOM к моменту подсветки.
    setEditMode(false);
    if (canHaveForbiddenForms) setShowAliasForm(true);
    window.setTimeout(
      () =>
        runTour(
          buildTerminologyItemTourSteps({ canHaveForbiddenForms, isReadOnly }),
        ),
      60,
    );
  }

  function startEdit(): void {
    setDraft({
      source_text: item.source_text ?? "",
      target_text: item.target_text ?? "",
      source_lang: item.source_lang ?? "",
      target_lang: item.target_lang ?? "",
      domain: item.domain ?? "",
      confidence: item.confidence != null ? String(item.confidence) : "",
      notes: item.notes ?? "",
      source_expansion: item.source_expansion ?? "",
      target_abbreviation: item.target_abbreviation ?? "",
      context_words: (item.context_words_stems ?? []).join(", "),
    });
    setEditMode(true);
  }
  function cancelEdit(): void {
    setEditMode(false);
  }
  function setField(key: FieldKey, value: string): void {
    setDraft((d) => ({ ...d, [key]: value }));
  }
  function submitEdit(): void {
    // Только изменённые поля (по сравнению с item).
    const payload: Record<string, unknown> = {};
    for (const k of EDITABLE_FIELDS) {
      const original =
        k === "confidence"
          ? item.confidence != null
            ? String(item.confidence)
            : ""
          : k === "context_words"
            ? (item.context_words_stems ?? []).join(", ")
            : ((item as unknown as Record<string, unknown>)[k] as string) ?? "";
      const next = draft[k];
      if (next !== original) {
        if (k === "confidence") {
          const num = Number(next);
          if (!Number.isFinite(num)) {
            toast("Confidence должен быть числом", "error");
            return;
          }
          payload[k] = num;
        } else {
          payload[k] = next;
        }
      }
    }
    if (Object.keys(payload).length === 0) {
      toast("Нет изменений", "info");
      setEditMode(false);
      return;
    }
    if (
      savePrevAsAlias &&
      (item.item_type === "glossary_term" || item.item_type === "abbreviation")
    ) {
      payload["save_previous_as_alias"] = true;
    }
    updateMutation.mutate(payload);
  }

  async function handleSuggestSenses(): Promise<void> {
    const surface = (item.source_text ?? "").trim();
    if (!surface) {
      toast("Нет surface аббревиатуры", "error");
      return;
    }
    setSuggesting(true);
    setSuggestions(null);
    try {
      const res = await suggestAbbrevSenses(surface);
      if (!res.ok) {
        toast(res.error || "LLM-подсказка не удалась", "error");
        return;
      }
      setSuggestions(res.data.senses ?? []);
      if ((res.data.senses ?? []).length === 0) {
        toast("LLM не вернул смыслов", "info");
      }
    } finally {
      setSuggesting(false);
    }
  }

  async function handleProbe(): Promise<void> {
    const text = probeText.trim();
    if (!text) {
      toast("Вставь пример-предложение", "info");
      return;
    }
    setProbing(true);
    setProbeResult(null);
    try {
      // Текущее состояние термина: draft в режиме правки (проверка НЕсохранённых
      // правок), иначе сохранённое. context_words тоже из draft/стеммов.
      const term = {
        source_abbreviation: editMode ? draft.source_text : item.source_text ?? "",
        source_lang: editMode ? draft.source_lang : item.source_lang ?? "",
        source_expansion: editMode
          ? draft.source_expansion
          : item.source_expansion ?? "",
        target_abbreviation: editMode
          ? draft.target_abbreviation
          : item.target_abbreviation ?? "",
        target_lang: editMode ? draft.target_lang : item.target_lang ?? "",
        target_expansion: editMode ? draft.target_text : item.target_text ?? "",
        domain: editMode ? draft.domain : item.domain ?? "",
        context_words: editMode
          ? draft.context_words
          : (item.context_words_stems ?? []).join(", "),
      };
      // Направление по скрипту входного текста: кириллица → RU→EN, иначе EN→RU.
      // Было жёстко EN→RU → русские аббревиатуры (МАЖБП, ПОАК) не матчились.
      const isCyrillic = /[А-Яа-яЁё]/.test(text);
      const res = await probeTerminology({
        text,
        source_lang: isCyrillic ? "ru" : "en",
        target_lang: isCyrillic ? "en" : "ru",
        term,
        include_translation: true,
      });
      if (!res.ok) {
        toast(res.error || "Проба не удалась", "error");
        return;
      }
      setProbeResult(res.data);
    } finally {
      setProbing(false);
    }
  }

  const targetTextChanged =
    editMode &&
    (item.item_type === "glossary_term" || item.item_type === "abbreviation") &&
    (item.target_text ?? "") !== (draft.target_text ?? "") &&
    (draft.target_text ?? "").trim().length > 0;

  return (
    <div style={{ padding: "var(--sp-3)", maxWidth: 1200 }}>
      <header
        data-tour="item-header"
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          marginBottom: "var(--sp-3)",
          flexWrap: "wrap",
        }}
      >
        <Link to="/admin/terminology" className="mt-btn mt-btn--sm mt-btn--ghost">
          ‹ К терминологии
        </Link>
        <button
          className="mt-btn mt-btn--sm mt-btn--ghost"
          onClick={startItemTour}
          type="button"
          title="Тур: как настроить принудительную замену"
        >
          ? Тур
        </button>
        <span className={`mt-itemtype mt-itemtype--${item.item_type}`}>
          {itemTypeLabel(item.item_type)}
        </span>
        {item.status && (
          <span className={`mt-badge ${badgeForStatus(item.status)}`}>
            {statusLabel(item.status)}
          </span>
        )}
        <span className="mt-toolbar__sep" style={{ flex: 1 }} />
        <span className="mt-mono mt-text-tight mt-muted">{item.item_id}</span>
        <button
          className="mt-icon-btn mt-icon-btn--bordered"
          title="Скопировать item_id"
          onClick={() => {
            navigator.clipboard?.writeText(item.item_id);
            toast("Скопировано", "success");
          }}
          type="button"
        >
          ⧉
        </button>
        <div className="mt-divider--v" />
        {!editMode ? (
          <>
            <button
              className="mt-btn mt-btn--secondary"
              onClick={startEdit}
              disabled={isReadOnly}
              title={isReadOnly ? "Кандидаты редактируются через review-flow" : undefined}
              type="button"
            >
              Изменить
            </button>
            {item.item_type === "abbrev_candidate" && item.status === "candidate" && (
              <button
                className="mt-btn mt-btn--primary"
                onClick={() => {
                  const ok = window.confirm(
                    `Отправить в очередь ревью?\n\n` +
                      `«${item.source_text}» → «${item.target_text}»\n\n` +
                      "Reviewer сможет одобрить/отредактировать запись, " +
                      "после чего она переедет в активный словарь и начнёт " +
                      "работать в переводе.",
                  );
                  if (ok) promoteMutation.mutate();
                }}
                disabled={promoteMutation.isPending}
                type="button"
                title="Создать review_item из этой candidate-записи"
              >
                {promoteMutation.isPending
                  ? "Отправляю…"
                  : "Отправить в ревью"}
              </button>
            )}
            {item.item_type === "term_alias" && (
              <button
                className="mt-btn mt-btn--danger mt-btn--ghost"
                onClick={() => {
                  const aliasText = item.source_text || "";
                  const canonicalText = item.canonical_text || item.target_text || "";
                  const confirmed = window.confirm(
                    `Удалить alias?\n\n«${aliasText}» → «${canonicalText}»\n\n` +
                      "Resolver перестанет заменять старую форму на canonical.\n" +
                      "Действие необратимо.",
                  );
                  if (confirmed) deleteMutation.mutate();
                }}
                disabled={deleteMutation.isPending}
                type="button"
                title="Hard delete — для glossary_term используй смену status на 'deprecated'"
              >
                {deleteMutation.isPending ? "Удаление…" : "Удалить"}
              </button>
            )}
          </>
        ) : (
          <>
            {targetTextChanged && (
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  fontSize: "var(--fs-sm, 13px)",
                  color: "var(--text)",
                  background: "var(--yellow-50, #fefce8)",
                  border: "1px solid var(--yellow-300, #fde047)",
                  borderRadius: 6,
                  padding: "4px 10px",
                  cursor: "pointer",
                }}
                title={
                  "Создаст term_alias со старым переводом и пометит его как " +
                  "replace_with_canonical=true — resolver будет автоматически " +
                  "заменять старую форму в выводе MT на новый canonical. " +
                  "Включай только при смене смысла, не для исправлений опечаток."
                }
              >
                <input
                  type="checkbox"
                  checked={savePrevAsAlias}
                  onChange={(e) => setSavePrevAsAlias(e.target.checked)}
                />
                <span>
                  Сохранить «{item.target_text}» как replace-alias
                </span>
              </label>
            )}
            <button
              className="mt-btn mt-btn--ghost"
              onClick={cancelEdit}
              type="button"
            >
              Отмена
            </button>
            <button
              className="mt-btn mt-btn--primary"
              onClick={submitEdit}
              disabled={updateMutation.isPending}
              type="button"
            >
              {updateMutation.isPending ? "Сохранение…" : "Сохранить"}
            </button>
          </>
        )}
      </header>

      <h1
        style={{
          margin: "0 0 var(--sp-3)",
          display: "flex",
          gap: 12,
          alignItems: "baseline",
          flexWrap: "wrap",
        }}
      >
        {item.source_lang && (
          <span className="mt-review__lang-tag">
            {langTagLabel(item.source_lang as never)}
          </span>
        )}
        <span>{item.source_text ?? "—"}</span>
        {item.target_text && (
          <>
            <span className="rl-arrow">→</span>
            {item.target_lang && (
              <span className="mt-review__lang-tag">
                {langTagLabel(item.target_lang as never)}
              </span>
            )}
            <span style={{ color: "var(--text-soft)" }}>{item.target_text}</span>
          </>
        )}
      </h1>

      {!editMode && !isReadOnly && (
        <div data-tour="usage-block">
          <TerminologyUsageBlock item={item} reviewer={reviewer} />
        </div>
      )}

      {!editMode &&
        !isReadOnly &&
        (item.item_type === "glossary_term" ||
          item.item_type === "abbreviation") && (
          <section className="rf-section" data-tour="forbidden-forms">
            <div className="rf-section__head">
              <span>Запрещённые формы</span>
              <span className="mt-toolbar__sep" style={{ flex: 1 }} />
              {!showAliasForm && (
                <button
                  className="mt-btn mt-btn--sm mt-btn--ghost"
                  onClick={() => setShowAliasForm(true)}
                  type="button"
                >
                  + Добавить
                </button>
              )}
            </div>
            <div className="rf-section__body">
              <div
                className="mt-text-tight mt-muted"
                style={{ marginBottom: 10, textTransform: "none", letterSpacing: 0 }}
              >
                Если переводчик иногда выдаёт неправильный вариант (например,
                «прямые пероральные антикоагулянты» вместо канонического
                «прямые оральные антикоагулянты», или «ППАК» вместо «ПОАК») —
                добавь его сюда. Resolver будет автоматически менять
                эту форму на canonical при переводе.
              </div>
              {replaceAliases.length > 0 && (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 6,
                    marginBottom: 10,
                  }}
                >
                  {replaceAliases.map((a) => (
                    <div
                      key={a.item_id || a.alias_text}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        padding: "6px 10px",
                        background: "var(--panel)",
                        border: "1px solid var(--border-default, #d0d5dd)",
                        borderRadius: 6,
                      }}
                    >
                      <span style={{ color: "var(--red-700, #b91c1c)" }}>
                        {a.alias_text}
                      </span>
                      <span className="rl-arrow">→</span>
                      <span style={{ color: "var(--text)" }}>
                        {a.canonical_text}
                      </span>
                      {a.exact_replacement && (
                        <span
                          className="mt-badge mt-badge--neutral mt-badge--no-dot"
                          title="Точная подстановка без морфологии (для аббревиатур)"
                        >
                          точная
                        </span>
                      )}
                      <span className="mt-toolbar__sep" style={{ flex: 1 }} />
                      <button
                        className="mt-btn mt-btn--sm mt-btn--ghost"
                        onClick={() => {
                          if (!a.item_id) return;
                          const ok = window.confirm(
                            `Удалить «${a.alias_text}» → «${a.canonical_text}»?\n\n` +
                              "Resolver перестанет автоматически заменять эту форму. " +
                              "Действие необратимо.",
                          );
                          if (ok) aliasDeleteMutation.mutate(a.item_id);
                        }}
                        disabled={aliasDeleteMutation.isPending || !a.item_id}
                        type="button"
                        title="Удалить запрещённую форму"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
              {showAliasForm && (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 8,
                    padding: 12,
                    background: "var(--panel)",
                    border: "1px solid var(--border-default, #d0d5dd)",
                    borderRadius: 8,
                  }}
                >
                  <label
                    className="mt-text-tight"
                    style={{ display: "flex", flexDirection: "column", gap: 4 }}
                  >
                    <span>Запрещённая форма (как переводчик её выдаёт):</span>
                    <input
                      className="mt-input"
                      type="text"
                      data-tour="alias-input"
                      value={aliasInput}
                      onChange={(e) => setAliasInput(e.target.value)}
                      placeholder={
                        item.item_type === "abbreviation"
                          ? "например, ППАК или прямые пероральные антикоагулянты"
                          : "например, гипертония"
                      }
                      autoFocus
                    />
                  </label>
                  {item.item_type === "abbreviation" && (
                    <fieldset
                      style={{ border: "none", padding: 0, margin: 0 }}
                    >
                      <legend
                        className="mt-text-tight mt-muted"
                        style={{
                          marginBottom: 4,
                          textTransform: "none",
                          letterSpacing: 0,
                        }}
                      >
                        Заменять на:
                      </legend>
                      <label
                        style={{
                          display: "flex",
                          gap: 6,
                          alignItems: "center",
                          marginBottom: 4,
                        }}
                      >
                        <input
                          type="radio"
                          checked={aliasReplaceTo === "text"}
                          onChange={() => setAliasReplaceTo("text")}
                        />
                        <span>
                          полный перевод: <strong>{item.target_text}</strong>{" "}
                          <span className="mt-muted">
                            (по падежам через pymorphy)
                          </span>
                        </span>
                      </label>
                      {item.target_abbreviation && (
                        <label
                          style={{
                            display: "flex",
                            gap: 6,
                            alignItems: "center",
                          }}
                        >
                          <input
                            type="radio"
                            checked={aliasReplaceTo === "abbrev"}
                            onChange={() => setAliasReplaceTo("abbrev")}
                          />
                          <span>
                            сокращение:{" "}
                            <strong>{item.target_abbreviation}</strong>{" "}
                            <span className="mt-muted">
                              (точная подстановка, без морфологии)
                            </span>
                          </span>
                        </label>
                      )}
                    </fieldset>
                  )}
                  <div
                    style={{
                      display: "flex",
                      gap: 8,
                      justifyContent: "flex-end",
                      marginTop: 4,
                    }}
                  >
                    <button
                      className="mt-btn mt-btn--ghost mt-btn--sm"
                      onClick={() => {
                        setShowAliasForm(false);
                        setAliasInput("");
                      }}
                      type="button"
                    >
                      Отмена
                    </button>
                    <button
                      className="mt-btn mt-btn--primary mt-btn--sm"
                      data-tour="alias-submit"
                      onClick={() => {
                        const aliasText = aliasInput.trim();
                        if (!aliasText) {
                          toast("Введите запрещённую форму", "error");
                          return;
                        }
                        const targetAbbrev = item.target_abbreviation;
                        const useAbbrev =
                          item.item_type === "abbreviation" &&
                          aliasReplaceTo === "abbrev" &&
                          !!targetAbbrev;
                        const canonicalText = useAbbrev
                          ? (targetAbbrev as string)
                          : item.target_text || "";
                        if (!canonicalText) {
                          toast("Не удалось определить canonical", "error");
                          return;
                        }
                        addAliasMutation.mutate({
                          alias_text: aliasText,
                          canonical_text: canonicalText,
                          exact: useAbbrev,
                        });
                      }}
                      disabled={addAliasMutation.isPending}
                      type="button"
                    >
                      {addAliasMutation.isPending ? "Добавляю…" : "Добавить"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </section>
        )}

      {!editMode && siblings.length > 0 && (
        <section
          className="rf-section"
          style={{
            background: "var(--yellow-50, #fefce8)",
            borderColor: "var(--yellow-400, #facc15)",
          }}
        >
          <div
            className="rf-section__head"
            style={{
              background: "var(--yellow-100, #fef9c3)",
              color: "var(--yellow-800, #854d0e)",
            }}
          >
            <span aria-hidden style={{ marginRight: 6 }}>
              ⚠
            </span>
            <span>
              Другие смыслы той же{" "}
              {item.item_type === "abbreviation" ? "аббревиатуры" : "формы"}
            </span>
            <span className="mt-toolbar__sep" style={{ flex: 1 }} />
            <span
              className="mt-badge mt-badge--warning mt-badge--no-dot"
              style={{ background: "var(--yellow-200, #fef08a)" }}
            >
              {siblings.length}
            </span>
          </div>
          <div className="rf-section__body">
            <div
              className="mt-text-tight"
              style={{
                marginBottom: 10,
                textTransform: "none",
                letterSpacing: 0,
                color: "var(--yellow-900, #713f12)",
              }}
            >
              {(() => {
                const approvedCount = siblings.filter(
                  (s) => s.status === "approved",
                ).length;
                const candidateCount = siblings.filter(
                  (s) => s.status === "candidate",
                ).length;
                if (approvedCount > 0 && candidateCount === 0) {
                  return (
                    <>
                      «{item.source_text}» одобрено как несколько разных
                      терминов в разных доменах. Resolver выбирает по
                      domain / context; ты можешь переключиться на любую
                      запись ниже.
                    </>
                  );
                }
                if (approvedCount === 0 && candidateCount > 0) {
                  return (
                    <>
                      «{item.source_text}» встречается в нескольких
                      candidate-записях с разными определениями. Ни одна
                      ещё не одобрена — для активации в переводе нужно
                      каждую отправить в ревью и пройти review-flow.
                    </>
                  );
                }
                return (
                  <>
                    «{item.source_text}» — несколько вариантов этой формы:
                    {approvedCount > 0 && ` ${approvedCount} одобрено`}
                    {approvedCount > 0 && candidateCount > 0 && ","}
                    {candidateCount > 0 && ` ${candidateCount} в кандидатах`}.
                    Resolver учитывает только approved-записи.
                  </>
                );
              })()}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {siblings.map((s) => (
                <Link
                  key={s.item_id}
                  to={`/admin/terminology/${encodeURIComponent(s.item_id)}`}
                  className="mt-llm-card"
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 4,
                    textDecoration: "none",
                    color: "inherit",
                    padding: "8px 12px",
                  }}
                >
                  <div className="mt-llm-card__head">
                    {s.domain && (
                      <span className="mt-badge mt-badge--info mt-badge--no-dot">
                        {s.domain}
                      </span>
                    )}
                    <span className="mt-mono mt-text-tight">
                      {s.item_id.slice(0, 12)}
                    </span>
                    {s.status && s.status !== "approved" && (
                      <span className="mt-badge mt-badge--neutral mt-badge--no-dot">
                        {s.status}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: "var(--fs-md)" }}>
                    <strong>
                      {s.source_expansion ?? s.source_text ?? "—"}
                    </strong>
                    {(s.target_text || s.target_expansion) && (
                      <>
                        {" → "}
                        <span className="mt-muted">
                          {s.target_expansion ?? s.target_text}
                        </span>
                      </>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <section
        className="rf-section"
        style={
          editMode
            ? { background: "var(--panel)", borderColor: "var(--blue-300, var(--blue-100))" }
            : { background: "var(--panel)" }
        }
      >
        <div
          className="rf-section__head"
          style={
            editMode
              ? { background: "var(--blue-50)", color: "var(--blue-700)" }
              : undefined
          }
        >
          <span>{editMode ? "Режим правки" : "Поля"}</span>
          <span className="mt-toolbar__sep" style={{ flex: 1 }} />
          {item.confidence != null && !editMode && (
            <span className="mt-mono mt-text-tight">
              conf: {item.confidence.toFixed(2)}
            </span>
          )}
        </div>
        <div className="rf-section__body">
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "12px 16px",
            }}
          >
            <Field
              label="Оригинал"
              langTag={editMode ? draft.source_lang : item.source_lang}
              value={editMode ? draft.source_text : (item.source_text ?? "")}
              editable={editMode}
              onChange={(v) => setField("source_text", v)}
            />
            <Field
              label="Перевод"
              langTag={editMode ? draft.target_lang : item.target_lang}
              value={editMode ? draft.target_text : (item.target_text ?? "")}
              editable={editMode}
              onChange={(v) => setField("target_text", v)}
            />
            <Field
              label="Язык оригинала"
              value={editMode ? draft.source_lang : (item.source_lang ?? "")}
              editable={editMode}
              onChange={(v) => setField("source_lang", v)}
              hint="en / ru / la / und"
            />
            <Field
              label="Язык перевода"
              value={editMode ? draft.target_lang : (item.target_lang ?? "")}
              editable={editMode}
              onChange={(v) => setField("target_lang", v)}
              hint="en / ru / la / und"
            />
            <Field
              label="Домен"
              value={editMode ? draft.domain : (item.domain ?? "")}
              editable={editMode}
              onChange={(v) => setField("domain", v)}
            />
            <Field
              label="Confidence"
              value={
                editMode
                  ? draft.confidence
                  : item.confidence != null
                    ? item.confidence.toFixed(3)
                    : ""
              }
              editable={editMode}
              onChange={(v) => setField("confidence", v)}
              hint="0.0 — 1.0"
            />
            {item.item_type === "abbreviation" && (
              <>
                <Field
                  label="Расшифровка (source)"
                  langTag={editMode ? draft.source_lang : item.source_lang}
                  value={
                    editMode
                      ? draft.source_expansion
                      : (item.source_expansion ?? "")
                  }
                  editable={editMode}
                  onChange={(v) => setField("source_expansion", v)}
                  hint="полная форма в исходном языке (например, glycated hemoglobin)"
                />
                <Field
                  label="Сокращение (target)"
                  langTag={editMode ? draft.target_lang : item.target_lang}
                  value={
                    editMode
                      ? draft.target_abbreviation
                      : (item.target_abbreviation ?? "")
                  }
                  editable={editMode}
                  onChange={(v) => setField("target_abbreviation", v)}
                  hint="например, HbA1c (часто оставляют латиницей)"
                />
                <div style={{ gridColumn: "span 2" }}>
                  <Field
                    label="Контекст для развязки смысла (через запятую)"
                    value={
                      editMode
                        ? draft.context_words
                        : (item.context_words_stems ?? []).join(", ")
                    }
                    editable={editMode}
                    onChange={(v) => setField("context_words", v)}
                    multiline
                    hint="слова-маркеры этого смысла (ratio, hazard, ci) — стеммятся при сохранении; нужны для полисемичных (HR → ОР vs ЧСС)"
                  />
                  <details style={{ marginTop: 6, fontSize: "var(--fs-sm)" }}>
                    <summary
                      className="mt-muted"
                      style={{ cursor: "pointer", userSelect: "none" }}
                    >
                      Как это работает? Примеры
                    </summary>
                    <div
                      style={{
                        marginTop: 8,
                        display: "flex",
                        flexDirection: "column",
                        gap: 8,
                      }}
                    >
                      {[
                        {
                          text: "…major bleeding (HR 0.83, 95% CI 0.71–0.96) compared with warfarin",
                          markers: "ratio · ci · compared · hazard",
                          sense: "ОР — отношение рисков",
                        },
                        {
                          text: "Resting HR was 72 bpm with sinus rhythm at baseline",
                          markers: "bpm · resting · sinus · pulse",
                          sense: "ЧСС — частота сердечных сокращений",
                        },
                        {
                          text: "АД 165/95 мм рт. ст., гипертензия 2 ст.",
                          markers: "мм · рт · гипертензия · систолический",
                          sense: "артериальное давление (BP)",
                        },
                        {
                          text: "АД с выраженным зудом и экземой на коже",
                          markers: "кожа · зуд · экзема · дерматит",
                          sense: "атопический дерматит",
                        },
                      ].map((ex, i) => (
                        <div
                          key={i}
                          className="rf-example"
                          style={{ padding: 8 }}
                        >
                          <div style={{ overflowWrap: "anywhere" }}>
                            «{ex.text}»
                          </div>
                          <div className="mt-muted" style={{ marginTop: 3 }}>
                            маркеры: {ex.markers} → <b>{ex.sense}</b>
                          </div>
                        </div>
                      ))}
                      <div className="mt-muted">
                        Резолвер берёт окно слов вокруг аббревиатуры, стеммит и
                        сравнивает с маркерами каждого смысла — побеждает
                        максимальное пересечение (при пороге и отрыве от
                        второго).
                      </div>
                    </div>
                  </details>
                  {editMode && (
                    <div
                      style={{
                        marginTop: 8,
                        display: "flex",
                        gap: 8,
                        alignItems: "center",
                        flexWrap: "wrap",
                      }}
                    >
                      <button
                        type="button"
                        className="mt-btn mt-btn--sm mt-btn--secondary"
                        disabled={suggesting}
                        onClick={() => void handleSuggestSenses()}
                      >
                        {suggesting ? "Думаю…" : "💡 Подсказать смыслы (LLM)"}
                      </button>
                      <span
                        className="mt-muted"
                        style={{ fontSize: "var(--fs-sm)" }}
                      >
                        RAG по нашей терминологии + LLM
                      </span>
                    </div>
                  )}
                  {editMode && suggestions && suggestions.length > 0 && (
                    <div
                      style={{
                        marginTop: 10,
                        display: "flex",
                        flexDirection: "column",
                        gap: 8,
                      }}
                    >
                      {suggestions.map((s, i) => (
                        <div
                          key={i}
                          className="rf-example"
                          style={{ fontSize: "var(--fs-sm)", padding: 8 }}
                        >
                          <div style={{ fontWeight: 600 }}>
                            {s.source_expansion} → {s.target_expansion}
                            {s.target_abbreviation
                              ? ` (${s.target_abbreviation})`
                              : ""}
                            <span className="mt-muted"> · {s.domain}</span>
                          </div>
                          <div
                            style={{ marginTop: 4, overflowWrap: "anywhere" }}
                          >
                            <span className="mt-muted">context: </span>
                            {(s.context_words ?? []).join(", ")}
                          </div>
                          <button
                            type="button"
                            className="mt-btn mt-btn--sm mt-btn--ghost"
                            style={{ marginTop: 6 }}
                            onClick={() =>
                              setField(
                                "context_words",
                                (s.context_words ?? []).join(", "),
                              )
                            }
                          >
                            Применить context →
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div style={{ gridColumn: "span 2" }}>
                  <div
                    className="mt-text-tight mt-muted"
                    style={{ marginBottom: 6, fontWeight: 600 }}
                  >
                    Проверка перевода на примере
                  </div>
                  <textarea
                    className="mt-input"
                    rows={2}
                    placeholder="Вставь фразу (RU или EN) с этой аббревиатурой — увидишь, какой смысл сработает и как переведётся (ничего не сохраняя)"
                    value={probeText}
                    onChange={(e) => setProbeText(e.target.value)}
                    style={{ width: "100%", fontSize: "var(--fs-sm)" }}
                  />
                  <div
                    style={{
                      marginTop: 6,
                      display: "flex",
                      gap: 8,
                      alignItems: "center",
                      flexWrap: "wrap",
                    }}
                  >
                    <button
                      type="button"
                      className="mt-btn mt-btn--sm mt-btn--secondary"
                      disabled={probing}
                      onClick={() => void handleProbe()}
                    >
                      {probing ? "Проверяю…" : "Проверить"}
                    </button>
                    {editMode && (
                      <span
                        className="mt-muted"
                        style={{ fontSize: "var(--fs-sm)" }}
                      >
                        учитывает несохранённые правки контекста
                      </span>
                    )}
                  </div>
                  {probeResult && (
                    <div
                      className="rf-example"
                      style={{
                        marginTop: 8,
                        padding: 8,
                        fontSize: "var(--fs-sm)",
                      }}
                    >
                      {probeResult.terms.length > 0 ? (
                        <div>
                          <span className="mt-muted">сработал смысл: </span>
                          {probeResult.terms.map((t, i) => (
                            <b key={i}>
                              {t.surface} →{" "}
                              {t.output_abbreviation ?? t.target_text}
                              {t.domain ? ` (${t.domain})` : ""}
                              {i < probeResult.terms.length - 1 ? "; " : ""}
                            </b>
                          ))}
                        </div>
                      ) : (
                        <div className="mt-muted">
                          термин не сматчился в этом тексте
                        </div>
                      )}
                      {probeResult.expanded_text && (
                        <div
                          style={{ marginTop: 4, overflowWrap: "anywhere" }}
                        >
                          <span className="mt-muted">в MT уйдёт: </span>
                          {probeResult.expanded_text}
                        </div>
                      )}
                      {probeResult.translation && (
                        <div
                          style={{ marginTop: 4, overflowWrap: "anywhere" }}
                        >
                          <span className="mt-muted">перевод: </span>
                          <b>{probeResult.translation}</b>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </>
            )}
            <div style={{ gridColumn: "span 2" }}>
              <Field
                label="Заметки"
                value={editMode ? draft.notes : (item.notes ?? "")}
                editable={editMode}
                onChange={(v) => setField("notes", v)}
                multiline
              />
            </div>
            {!editMode && (
              <KvRow label="Источник (key)" value={item.source_key} mono />
            )}
            {!editMode && (
              <KvRow label="Source ref" value={item.source_ref} mono />
            )}
            {!editMode && (
              <KvRow
                label="Concept key"
                value={
                  (item as unknown as Record<string, unknown>)[
                    "concept_key"
                  ] as string | undefined
                }
                mono
              />
            )}
            {!editMode && (
              <KvRow label="Canonical text" value={item.canonical_text} />
            )}
          </div>
        </div>
      </section>

      {item.parent_concept && !editMode && (
        <section className="rf-section" style={{ background: "var(--panel)" }}>
          <div className="rf-section__head">
            <span>Родительский концепт</span>
            <span className="mt-toolbar__sep" style={{ flex: 1 }} />
            <span className="mt-text-tight mt-muted">
              alias принадлежит этому glossary-термину
            </span>
          </div>
          <div className="rf-section__body">
            <Link
              to={
                item.parent_concept.item_id
                  ? `/admin/terminology/${encodeURIComponent(item.parent_concept.item_id)}`
                  : "#"
              }
              style={{
                display: "flex",
                gap: 8,
                alignItems: "baseline",
                flexWrap: "wrap",
                color: "var(--blue-700)",
                textDecoration: "none",
                fontSize: "var(--fs-md)",
              }}
            >
              {item.parent_concept.source_lang && (
                <span className="mt-review__lang-tag">
                  {langTagLabel(item.parent_concept.source_lang as never)}
                </span>
              )}
              <span>{item.parent_concept.source_text ?? "—"}</span>
              {item.parent_concept.target_text && (
                <>
                  <span className="rl-arrow">→</span>
                  {item.parent_concept.target_lang && (
                    <span className="mt-review__lang-tag">
                      {langTagLabel(item.parent_concept.target_lang as never)}
                    </span>
                  )}
                  <span>{item.parent_concept.target_text}</span>
                </>
              )}
              {item.parent_concept.domain && (
                <span
                  className="mt-text-tight mt-muted"
                  style={{ fontSize: "var(--fs-xs)" }}
                >
                  · {item.parent_concept.domain}
                </span>
              )}
              {item.parent_concept.source_key && (
                <span
                  className="mt-mono mt-text-tight mt-muted"
                  style={{ fontSize: "var(--fs-xs)" }}
                >
                  · {item.parent_concept.source_key}
                </span>
              )}
              <span style={{ color: "var(--blue-500)", marginLeft: "auto" }}>
                Открыть →
              </span>
            </Link>
          </div>
        </section>
      )}

      {item.external_ids && Object.keys(item.external_ids).length > 0 && !editMode && (
        <section className="rf-section" style={{ background: "var(--panel)" }}>
          <div className="rf-section__head">
            <span>Внешние идентификаторы</span>
            {(item.external_ids as Record<string, unknown>).source_role ===
              "trusted_phrase_memory" && (
              <>
                <span className="mt-toolbar__sep" style={{ flex: 1 }} />
                <span className="mt-badge mt-badge--info mt-badge--no-dot">
                  trusted phrase memory
                </span>
              </>
            )}
          </div>
          <div className="rf-section__body">
            <div className="rf-ref-list">
              {Object.entries(item.external_ids).map(([k, v]) => (
                <span key={k} className="rf-ref">
                  <span className="mt-muted">{k}:</span>{" "}
                  {Array.isArray(v)
                    ? v.length <= 3
                      ? v.join(", ")
                      : `${v.slice(0, 3).join(", ")} +${v.length - 3}`
                    : typeof v === "object"
                      ? JSON.stringify(v).slice(0, 80)
                      : String(v)}
                </span>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="rf-section" style={{ background: "var(--panel)" }}>
        <details>
          <summary
            className="rf-section__head"
            style={{ cursor: "pointer", listStyle: "none", userSelect: "none" }}
          >
            <span>Все поля (техническая информация)</span>
          </summary>
          <div className="rf-section__body">
            <pre
              className="mt-mono mt-text-tight"
              style={{
                background: "var(--panel-soft)",
                padding: "8px 12px",
                borderRadius: "var(--r-sm)",
                overflow: "auto",
                fontSize: "var(--fs-xs)",
                margin: 0,
              }}
            >
              {JSON.stringify(item, null, 2)}
            </pre>
          </div>
        </details>
      </section>
    </div>
  );
}

function Field({
  label,
  langTag,
  value,
  editable = false,
  onChange,
  hint,
  multiline = false,
}: {
  label: string;
  langTag?: string | null;
  value: string;
  editable?: boolean;
  onChange?: (v: string) => void;
  hint?: string;
  multiline?: boolean;
}) {
  return (
    <div className="mt-field">
      <span className="mt-field__label">
        {langTag && langTag !== "und" && (
          <span className="mt-review__lang-tag">
            {langTagLabel(langTag as never)}
          </span>
        )}
        {label}
      </span>
      {multiline ? (
        <textarea
          className="mt-input"
          value={value}
          readOnly={!editable}
          onChange={editable && onChange ? (e) => onChange(e.target.value) : undefined}
          rows={3}
          style={{ resize: "vertical", minHeight: 60 }}
        />
      ) : (
        <input
          className="mt-input"
          value={value}
          readOnly={!editable}
          onChange={editable && onChange ? (e) => onChange(e.target.value) : undefined}
        />
      )}
      {hint && (
        <span className="mt-field__hint" style={{ fontSize: "var(--fs-xs)", color: "var(--muted)" }}>
          {hint}
        </span>
      )}
    </div>
  );
}

function KvRow({
  label,
  value,
  mono = false,
}: {
  label: string;
  value: unknown;
  mono?: boolean;
}) {
  if (value == null || value === "") return null;
  return (
    <div className="mt-field">
      <span className="mt-field__label">{label}</span>
      <input
        className={"mt-input" + (mono ? " mt-input--mono" : "")}
        value={String(value)}
        readOnly
      />
    </div>
  );
}

function badgeForStatus(status: string | null | undefined): string {
  switch (status) {
    case "approved":
      return "mt-badge--success";
    case "rejected":
      return "mt-badge--danger";
    case "needs_review":
    case "needs_second_review":
      return "mt-badge--warning";
    default:
      return "mt-badge--neutral";
  }
}
